import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Steps, Button, message } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { 
  PlanogramTemplate, 
  WizardStep, 
  WizardState, 
  ValidationError,
  PlanType,
  MilestoneConfig
} from '../../../../types/plan-template';
import PlanTypeSelection from './PlanTypeSelection';
import BasicInformation from './BasicInformation';
import MilestoneConfiguration from './MilestoneConfiguration';
import CycleConfiguration from './CycleConfiguration';
import ScopeConfiguration from './ScopeConfiguration';
import ValidationPreview from './ValidationPreview';
import { PlanogramTemplateService } from '../../../../services/planogramTemplateService';

interface TemplateWizardProps {
  visible: boolean;
  mode: 'create' | 'edit';
  templateId?: string;
  onComplete: (template: PlanogramTemplate) => void;
  onCancel: () => void;
}

const TemplateWizard: React.FC<TemplateWizardProps> = ({
  visible,
  mode,
  templateId,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation();
  
  const WIZARD_STEPS: Array<{
    key: WizardStep;
    title: string;
    description: string;
  }> = [
    {
      key: 'planType',
      title: t('planTemplate.wizard.steps.planType'),
      description: t('planTemplate.wizard.stepDescriptions.planType')
    },
    {
      key: 'basicInfo',
      title: t('planTemplate.wizard.steps.basicInfo'),
      description: t('planTemplate.wizard.stepDescriptions.basicInfo')
    },
    {
      key: 'milestones',
      title: t('planTemplate.wizard.steps.milestones'),
      description: t('planTemplate.wizard.stepDescriptions.milestones')
    },
    {
      key: 'cycle',
      title: t('planTemplate.wizard.steps.cycle'),
      description: t('planTemplate.wizard.stepDescriptions.cycle')
    },
    {
      key: 'scope',
      title: t('planTemplate.wizard.steps.scope'),
      description: t('planTemplate.wizard.stepDescriptions.scope')
    },
    {
      key: 'validation',
      title: t('planTemplate.wizard.steps.validation'),
      description: t('planTemplate.wizard.stepDescriptions.validation')
    }
  ];
  
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'planType',
    completedSteps: [],
    templateData: {},
    isValid: false,
    errors: []
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load existing template data for edit mode
  useEffect(() => {
    if (visible && mode === 'edit' && templateId) {
      loadTemplateData();
    } else if (visible && mode === 'create') {
      // Reset wizard state for new template
      setWizardState({
        currentStep: 'planType',
        completedSteps: [],
        templateData: {},
        isValid: false,
        errors: []
      });
    }
  }, [visible, mode, templateId]);

  const loadTemplateData = async () => {
    if (!templateId) return;

    setLoading(true);
    try {
      const response = await PlanogramTemplateService.getTemplateById(templateId);
      if (response.success) {
        const template = response.data;
        setWizardState(prev => ({
          ...prev,
          templateData: template,
          // Mark all steps as completed for edit mode
          completedSteps: WIZARD_STEPS.map(s => s.key)
        }));
      } else {
        message.error(response.message || '加载模板数据失败');
        onCancel();
      }
    } catch (error) {
      message.error('加载模板数据失败');
      onCancel();
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStepIndex = useCallback(() => {
    return WIZARD_STEPS.findIndex(step => step.key === wizardState.currentStep);
  }, [wizardState.currentStep, WIZARD_STEPS]);

  const updateTemplateData = useCallback((updates: Partial<PlanogramTemplate>) => {
    setWizardState(prev => ({
      ...prev,
      templateData: {
        ...prev.templateData,
        ...updates
      }
    }));
  }, []);

  const updateValidationState = useCallback((isValid: boolean, errors: ValidationError[] = []) => {
    setWizardState(prev => ({
      ...prev,
      isValid,
      errors
    }));
  }, []);

  const markStepCompleted = useCallback((step: WizardStep) => {
    setWizardState(prev => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(step) 
        ? prev.completedSteps 
        : [...prev.completedSteps, step]
    }));
  }, []);

  const goToStep = useCallback((step: WizardStep) => {
    setWizardState(prev => ({
      ...prev,
      currentStep: step
    }));
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < WIZARD_STEPS.length - 1) {
      const nextStep = WIZARD_STEPS[currentIndex + 1];
      markStepCompleted(wizardState.currentStep);
      goToStep(nextStep.key);
    }
  }, [getCurrentStepIndex, wizardState.currentStep, markStepCompleted, goToStep, WIZARD_STEPS]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      const previousStep = WIZARD_STEPS[currentIndex - 1];
      goToStep(previousStep.key);
    }
  }, [getCurrentStepIndex, goToStep, WIZARD_STEPS]);

  const handleStepClick = useCallback((stepIndex: number) => {
    const targetStep = WIZARD_STEPS[stepIndex];
    if (targetStep) {
      goToStep(targetStep.key);
    }
  }, [goToStep, WIZARD_STEPS]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const templateData = wizardState.templateData as PlanogramTemplate;
      
      let response;
      if (mode === 'create') {
        response = await PlanogramTemplateService.createTemplate({
          code: templateData.code,
          name: templateData.name,
          planType: templateData.planType,
          description: templateData.description,
          businessActivity: templateData.businessActivity,
          owner: templateData.owner,
          milestones: templateData.milestones || [],
          executionCycle: templateData.executionCycle,
          scopeDefinition: templateData.scopeDefinition,
          remark: templateData.remark
        });
      } else {
        response = await PlanogramTemplateService.updateTemplate(templateId!, {
          id: templateId!,
          code: templateData.code,
          name: templateData.name,
          planType: templateData.planType,
          description: templateData.description,
          businessActivity: templateData.businessActivity,
          owner: templateData.owner,
          milestones: templateData.milestones || [],
          executionCycle: templateData.executionCycle,
          scopeDefinition: templateData.scopeDefinition,
          remark: templateData.remark
        });
      }

      if (response.success) {
        message.success(t('planogramEditor.saveSuccess'));
        onComplete(response.data);
      } else {
        message.error(t('planogramEditor.saveFailed'));
      }
    } catch (error) {
      message.error(t('common.save') + t('common.failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('planTemplate.wizard.cancelConfirmTitle'),
      content: t('planTemplate.wizard.cancelConfirmContent'),
      onOk: onCancel
    });
  };

  const renderStepContent = () => {
    const commonProps = {
      templateData: wizardState.templateData,
      onDataChange: updateTemplateData,
      onValidationChange: updateValidationState,
      onNext: goToNextStep,
      onPrevious: goToPreviousStep
    };

    switch (wizardState.currentStep) {
      case 'planType':
        return (
          <PlanTypeSelection
            {...commonProps}
            planType={wizardState.templateData.planType}
            onPlanTypeChange={(planType: PlanType) => updateTemplateData({ planType })}
          />
        );

      case 'basicInfo':
        return (
          <BasicInformation
            {...commonProps}
            mode={mode}
            excludeId={mode === 'edit' ? templateId : undefined}
          />
        );

      case 'milestones':
        return (
          <MilestoneConfiguration
            {...commonProps}
            mode={mode}
            planType={wizardState.templateData.planType}
            milestones={wizardState.templateData.milestones || []}
            onMilestonesChange={(milestones: MilestoneConfig[]) => 
              updateTemplateData({ milestones })
            }
          />
        );

      case 'cycle':
        return (
          <CycleConfiguration
            {...commonProps}
          />
        );

      case 'scope':
        return (
          <ScopeConfiguration
            {...commonProps}
          />
        );

      case 'validation':
        return (
          <ValidationPreview
            {...commonProps}
          />
        );

      default:
        return null;
    }
  };

  const currentStepIndex = getCurrentStepIndex();
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1;

  return (
    <Modal
      title={
        mode === 'create' ? t('planTemplate.wizard.createTemplate') :
        t('planTemplate.edit') + t('planTemplate.template')
      }
      open={visible}
      width={1200}
      style={{ top: 20 }}
      bodyStyle={{ padding: 0 }}
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      maskClosable={false}
    >
      <div style={{ display: 'flex', height: '80vh' }}>
        {/* Left sidebar with steps */}
        <div style={{ 
          width: 280, 
          borderRight: '1px solid #f0f0f0',
          padding: '24px 16px',
          background: '#fafafa'
        }}>
          <Steps
            direction="vertical"
            current={currentStepIndex}
            onChange={handleStepClick}
            items={WIZARD_STEPS.map((step) => ({
              title: step.title,
              description: step.description,
              status: wizardState.completedSteps.includes(step.key) ? 'finish' :
                      step.key === wizardState.currentStep ? 'process' : 'wait'
            }))}
          />
        </div>

        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Content */}
          <div style={{ 
            flex: 1, 
            padding: '24px',
            overflow: 'auto'
          }}>
            {loading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '400px' 
              }}>
                {t('common.loading')}...
              </div>
            ) : (
              renderStepContent()
            )}
          </div>

          {/* Footer with navigation buttons */}
          <div style={{ 
            borderTop: '1px solid #f0f0f0',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fff'
          }}>
            <div>
              {wizardState.errors.length > 0 && (
                <span style={{ color: '#ff4d4f', fontSize: '14px' }}>
                  {t('planTemplate.wizard.errorCount', { count: wizardState.errors.length })}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button onClick={handleCancel} icon={<CloseOutlined />}>
                {t('common.cancel')}
              </Button>
              
              {!isFirstStep && (
                <Button onClick={goToPreviousStep}>
                  {t('common.previous')}
                </Button>
              )}

              {!isLastStep && (
                <Button 
                  type="primary" 
                  onClick={goToNextStep}
                  disabled={!wizardState.isValid}
                >
                  {t('common.next')}
                </Button>
              )}

              {isLastStep && (
                <Button 
                  type="primary" 
                  onClick={handleSave}
                  loading={saving}
                  disabled={!wizardState.isValid}
                  icon={<SaveOutlined />}
                >
                  {mode === 'create' ? t('planTemplate.wizard.createTemplate') : t('planTemplate.wizard.saveChanges')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TemplateWizard;